import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, TrendingUp, Wallet, Building2, DollarSign, Calculator, AlertCircle, AlertTriangle, Calendar as CalendarIcon, Pencil, TrendingDown, Shield, User, Users as UsersIcon, ArrowUpRight, ArrowDownRight, ArrowDown, ArrowUp, ArrowUpDown, CheckCircle, UserCircle, FileText } from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, getISOWeek, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LineChart, Line, PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { usePharmacies } from '../context/PharmacyContext';
import SimpleDateRangePicker from '../components/SimpleDateRangePicker';
import ModernDateRangePicker from '../components/ModernDateRangePicker';
import SuperAdminPharmacyFilter from '../components/SuperAdminPharmacyFilter';
import ExpensePharmacyFilter from '../components/ExpensePharmacyFilter';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import useGranularPermissions from '../hooks/useGranularPermissions';
import RevenueWizard from '../components/RevenueWizard';
import { toSafeISO } from '../utils/dateNormalize';
import CashWizard from '../components/CashWizard';
import SecuriteSocialeWizard from '../components/SecuriteSocialeWizard';
import { FilterableHeader, SortableHeader, useTableFilters } from '../components/TableFilters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function Revenues() {
  const { getPharmacyIdsParam, selectedPharmacyIds } = usePharmacies();
  const {
    canCreate,
    canUpdate,
    canDelete,
    canViewHistory,
    canViewTotals,
    canFilterPeriod,
    canViewAllUsers,
    canValidateCash
  } = useGranularPermissions();
  const [revenues, setRevenues] = useState([]);
  const [users, setUsers] = useState([]);
  const [cashClosures, setCashClosures] = useState([]);
  const [revenueEntries, setCAEntries] = useState([]); // Entrées de Chiffre d'Affaires
  const [loading, setLoading] = useState(true);
  const [chifaDialogOpen, setChifaDialogOpen] = useState(false);
  const [cashEntryDialogOpen, setCashEntryDialogOpen] = useState(false);
  const [caDialogOpen, setCADialogOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [cashWizardOpen, setCashWizardOpen] = useState(false);
  const [chifaWizardOpen, setChifaWizardOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('ca');
  const [adminUpdateDialogOpen, setAdminUpdateDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editingChifa, setEditingChifa] = useState(null);
  const [editingCA, setEditingCA] = useState(null);
  const [cashCurrentPage, setCashCurrentPage] = useState(1);
  const [caCurrentPage, setCaCurrentPage] = useState(1);
  const [revenuEspeceCurrentPage, setRevenuEspeceCurrentPage] = useState(1);
  const [chifaCurrentPage, setChifaCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const cashItemsPerPage = 10;
  const [currentUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to parse user from localStorage:', error);
      return null;
    }
  });
  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });

  // Évolution chart period selector: 'day' | 'week' | 'month'
  const [evolutionPeriod, setEvolutionPeriod] = useState('month');

  const [chifaFormData, setChifaFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    amount: '',
    description: '',
    reference: '',
    category: 'CNAS', // Renamed from chifa_category
  });

  const [cashFormData, setCashFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    user_id: currentUser?.role === 'caissier' ? currentUser.id : '',
    montant_caissier: '',
    notes: '',
  });

  const [caFormData, setCAFormData] = useState({
    date_start: format(new Date(), 'yyyy-MM-dd'),
    date_end: format(new Date(), 'yyyy-MM-dd'),
    amount: '',
    type: 'chiffre_affaires',
    notes: '',
  });

  const [adminFormData, setAdminFormData] = useState({
    montant_reel: '',
    notes: '',
  });

  useEffect(() => {
    if (!currentUser?.id) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      setLoading(false);
      return;
    }
    fetchData();
  }, [selectedPharmacyIds, dateRange, superAdminPharmacyFilter, currentUser]);

  // Auto-calculate date_end based on period_type
  const renderCountCa = React.useRef(0);
  useEffect(() => {
    if (caFormData.period_type && caFormData.date_start) {
      renderCountCa.current += 1;
      if (renderCountCa.current > 5) {
        console.error('FAILSAFE TRIGGERED in Revenues.js caFormData!');
        return;
      }
      const startDate = new Date(caFormData.date_start);
      let endDate;
      
      switch (caFormData.period_type) {
        case 'daily':
          endDate = startDate;
          break;
        case 'weekly':
          // Week from Saturday to Friday
          // Find the Saturday of the week
          const dayOfWeek = startDate.getDay(); // 0=Sun, 6=Sat
          const daysUntilSaturday = dayOfWeek === 6 ? 0 : (6 - dayOfWeek + 7) % 7;
          const saturday = addDays(startDate, -dayOfWeek + (dayOfWeek === 0 ? -1 : 6));
          endDate = addDays(saturday, 6); // Friday
          setCAFormData(prev => ({ ...prev, date_start: format(saturday, 'yyyy-MM-dd') }));
          break;
        case 'monthly':
          const monthStart = startOfMonth(startDate);
          endDate = endOfMonth(startDate);
          setCAFormData(prev => ({ ...prev, date_start: format(monthStart, 'yyyy-MM-dd') }));
          break;
        default:
          endDate = startDate;
      }
      
      setCAFormData(prev => ({ ...prev, date_end: format(endDate, 'yyyy-MM-dd') }));
    }
  }, [caFormData.period_type, caFormData.date_start]);

  const fetchData = async (customDateRange = null) => {
    setLoading(true);
    
    // Vérification session
    if (!currentUser?.id) {
      toast.error('Session invalide. Veuillez vous reconnecter.');
      setLoading(false);
      return;
    }
    
    try {
      // Use customDateRange if provided, otherwise use dateRange from state
      const rangeToUse = customDateRange || dateRange;
      const startDate = rangeToUse?.from || (rangeToUse?.from === null ? new Date(2000, 0, 1) : startOfMonth(new Date()));
      const endDate = rangeToUse?.to || (rangeToUse?.to === null ? new Date(2100, 11, 31) : endOfMonth(new Date()));
      
      const headers = {
        'X-User-Id': currentUser.id
      };
      
      // For super admin, use the pharmacy filter directly
      let pharmacyIds;
      if (currentUser?.role === 'super_admin') {
        // Super admin: use the filter value directly
        if (superAdminPharmacyFilter.length === 0) {
          pharmacyIds = 'all';
        } else {
          pharmacyIds = superAdminPharmacyFilter.join(',');
        }
      } else {
        // Regular admin: use pharmacy context
        pharmacyIds = getPharmacyIdsParam();
      }

      const results = await Promise.allSettled([
        axios.get(`${API}/revenues`, { 
          headers, 
          params: { 
            pharmacy_ids: pharmacyIds,
            start_date: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0).toISOString(),
            end_date: new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999).toISOString()
          } 
        }),
        axios.get(`${API}/users`, { params: { role: 'caissier', pharmacy_ids: pharmacyIds }, headers }),
        axios.get(`${API}/cash-closures`, { 
          headers: {
            ...headers,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }, 
          params: { 
            pharmacy_ids: pharmacyIds,
            start_date: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0).toISOString(),
            end_date: new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999).toISOString(),
            _t: new Date().getTime()
          } 
        }),
        axios.get(`${API}/revenue-entries`, { 
          headers,
          params: {
            start_date: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0).toISOString(),
            end_date: new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999).toISOString(),
            pharmacy_ids: pharmacyIds
          }
        }),
      ]);
      setRevenues(results[0].status === 'fulfilled' ? results[0].value.data : []);
      setUsers(results[1].status === 'fulfilled' ? results[1].value.data : []);
      setCashClosures(results[2].status === 'fulfilled' ? results[2].value.data : []);
      setCAEntries(results[3].status === 'fulfilled' ? results[3].value.data : []);
    } catch (error) {
      console.error('Erreur de chargement:', error);
      if (!currentUser?.id) {
        toast.error('Session invalide. Veuillez vous reconnecter.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('Accès refusé. Vérifiez vos permissions.');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCASubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser?.id) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    try {
      const headers = { 'X-User-Id': currentUser.id };
      
      const payload = {
        ...caFormData,
        date_start: toSafeISO(caFormData.date_start),
        date_end: toSafeISO(caFormData.date_end),
        amount: parseFloat(caFormData.amount),
      };

      if (editingCA) {
        await axios.put(`${API}/revenue-entries/${editingCA.id}`, payload, { headers });
        toast.success('Entrée modifiée avec succès!');
      } else {
        await axios.post(`${API}/revenue-entries`, payload, { headers });
        toast.success('Entrée ajoutée avec succès!');
      }

      setCADialogOpen(false);
      resetCAForm();
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        console.warn("Accès refusé. Droits administrateur requis.");
      } else {
        toast.error(editingCA ? "Erreur lors de la modification" : "Erreur lors de l'ajout de l'entrée");
      }
    }
  };

  const handleWizardSubmit = async (wizardData) => {
    if (!currentUser?.id) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    // Helper pour convertir une date locale à minuit UTC (évite le décalage timezone)
    const toMidnightUTC = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
    };
    
    try {
      const headers = { 'X-User-Id': currentUser.id };
      
      // Mapping des types wizard vers les types backend
      const typeMapping = {
        'ca': 'chiffre_affaires',
        'cash': 'revenu_espece',
        'caisse': 'revenu_caisse',
        'benefice_theorique': 'benefice_theorique',
        'benefice_net': 'benefice_net'
      };
      
      // Calculer les dates selon le type de période
      let dateStart, dateEnd;
      const today = new Date();
      
      if (wizardData.periodType === 'today') {
        dateStart = today;
        dateEnd = today;
      } else if (wizardData.periodType === 'week') {
        dateStart = startOfWeek(today, { weekStartsOn: 6 });
        dateEnd = endOfWeek(today, { weekStartsOn: 6 });
      } else if (wizardData.periodType === 'month') {
        dateStart = startOfMonth(today);
        dateEnd = endOfMonth(today);
      } else if (wizardData.periodType === 'global') {
        dateStart = new Date(2023, 0, 1);
        dateEnd = new Date();
      } else if (wizardData.periodType === 'custom') {
        // Utiliser les dates personnalisées
        dateStart = wizardData.customDates.start;
        dateEnd = wizardData.customDates.end;
      } else {
        dateStart = today;
        dateEnd = today;
      }
      
      const payload = {
        date_start: toMidnightUTC(dateStart),
        date_end: toMidnightUTC(dateEnd),
        amount: parseFloat(wizardData.amount),
        type: typeMapping[wizardData.type] || 'chiffre_affaires',
        notes: wizardData.note || '',
      };

      if (editingCA) {
        // Mode édition
        await axios.put(`${API}/revenue-entries/${editingCA.id}`, payload, { headers });
        toast.success('Entrée modifiée avec succès!');
      } else {
        // Mode création
        await axios.post(`${API}/revenue-entries`, payload, { headers });
        toast.success('Entrée ajoutée avec succès!');
      }
      
      setWizardOpen(false);
      setEditingCA(null);
      fetchData();
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      if (error.response?.status === 403) {
        console.warn("Accès refusé. Droits administrateur requis.");
      } else if (error.response?.data?.detail) {
        toast.error(`Erreur: ${error.response.data.detail}`);
      } else {
        toast.error("Erreur lors de l'ajout de l'entrée");
      }
    }
  };

  const handleChifaSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      const payload = {
        type: 'chifa',
        ...chifaFormData,
        amount: parseFloat(chifaFormData.amount),
        date: toSafeISO(chifaFormData.date),
      };

      if (editingChifa) {
        await axios.put(`${API}/revenues/${editingChifa.id}`, {
          ...editingChifa,
          ...payload,
        }, { headers });
        toast.success('Paiement Sécurité Sociale modifié avec succès!');
      } else {
        await axios.post(`${API}/revenues`, payload, { headers });
        toast.success('Paiement Sécurité Sociale ajouté avec succès!');
      }

      setChifaDialogOpen(false);
      resetChifaForm();
      fetchData();
    } catch (error) {
      toast.error(editingChifa ? "Erreur lors de la modification" : "Erreur lors de l'ajout du paiement Sécurité Sociale");
    }
  };

  const handleChifaWizardSubmit = async (wizardData) => {
    if (!currentUser?.id) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    const toSafeISO = (dateVal) => {
      if (!dateVal) return null;
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? null : d.toISOString();
    };
    
    try {
      const headers = { 'X-User-Id': currentUser.id };
      
      const isPaid = wizardData.is_paid === true || 
                     !!wizardData.bank_account_id || 
                     (wizardData.virement_amount !== '' && wizardData.virement_amount != null && parseFloat(wizardData.virement_amount) > 0);

      const payload = {
        type: 'chifa',
        date: toSafeISO(wizardData.date),
        amount: parseFloat(wizardData.amount),
        description: wizardData.description || '',
        reference: wizardData.reference || '',
        category: wizardData.category,
        bordereau_number: wizardData.bordereau_number || null,
        liquidation_date: toSafeISO(wizardData.liquidation_date),
        validation_date: toSafeISO(wizardData.validation_date),
        virement_date: toSafeISO(wizardData.virement_date),
        virement_amount: (wizardData.virement_amount !== '' && wizardData.virement_amount != null) ? parseFloat(wizardData.virement_amount) : null,
        montant_officine: (wizardData.montant_officine !== '' && wizardData.montant_officine != null) ? parseFloat(wizardData.montant_officine) : null,
        montant_rejete: (wizardData.montant_rejete !== '' && wizardData.montant_rejete != null) ? parseFloat(wizardData.montant_rejete) : null,
        encaissement_date: toSafeISO(wizardData.encaissement_date),
        is_paid: isPaid,
        bank_account_id: wizardData.bank_account_id || null,
        bank_amount: (wizardData.bank_amount !== '' && wizardData.bank_amount != null) ? parseFloat(wizardData.bank_amount) : null,
      };

      if (editingChifa) {
        // Mode édition
        await axios.put(`${API}/revenues/${editingChifa.id}`, payload, { headers });
        toast.success('Paiement Sécurité Sociale modifié avec succès!');
      } else {
        // Mode création
        await axios.post(`${API}/revenues`, payload, { headers });
        toast.success('Paiement Sécurité Sociale ajouté avec succès!');
      }
      
      setChifaWizardOpen(false);
      setEditingChifa(null);
      fetchData();
      
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.status === 403) {
        console.warn("Accès refusé. Droits administrateur requis.");
      } else if (error.response?.data?.detail) {
        toast.error(`Erreur: ${error.response.data.detail}`);
      } else {
        toast.error(editingChifa ? "Erreur lors de la modification" : "Erreur lors de l'ajout du paiement Sécurité Sociale");
      }
    }
  };

  const handleCashEntrySubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      if (selectedEntry) {
        // Update existing entry
        await axios.put(`${API}/cash-closures/${selectedEntry.id}`, {
          user_id: cashFormData.user_id,
          date: toSafeISO(cashFormData.date),
          montant_caissier: parseFloat(cashFormData.montant_caissier),
          notes: cashFormData.notes
        }, { headers });
        toast.success('Entrée de caisse modifiée avec succès!');
      } else {
        // Create new entry
        await axios.post(`${API}/cash-closures`, {
          user_id: cashFormData.user_id,
          date: toSafeISO(cashFormData.date),
          montant_caissier: parseFloat(cashFormData.montant_caissier),
          notes: cashFormData.notes
        }, { headers });
        toast.success('Montant de caisse enregistré!');
      }
      
      setCashEntryDialogOpen(false);
      setSelectedEntry(null);
      setCashFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        user_id: currentUser?.role === 'caissier' ? currentUser.id : '',
        montant_caissier: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(selectedEntry ? "Erreur lors de la modification" : "Erreur lors de l'enregistrement");
    }
  };

  const handleCashWizardSubmit = async (wizardData) => {
    if (!currentUser?.id) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      return;
    }
    
    // Helper pour convertir une date locale à minuit UTC
    const toMidnightUTC = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
    };
    
    try {
      const headers = { 'X-User-Id': currentUser.id };
      
      const payload = {
        user_id: wizardData.user_id,
        date: toMidnightUTC(wizardData.date),
        montant_caissier: parseFloat(wizardData.montant_caissier),
        notes: wizardData.notes || '',
      };

      if (selectedEntry) {
        // Mode édition
        await axios.put(`${API}/cash-closures/${selectedEntry.id}`, payload, { headers });
        
        // If editing a validated entry and montant_reel was changed, push it via /validate
        const isValidatedEdit = selectedEntry.status === 'validated';
        const newMontantReel = wizardData.montant_reel;
        const hasMontantReelChange = isValidatedEdit
          && newMontantReel !== '' && newMontantReel != null
          && parseFloat(newMontantReel) !== (selectedEntry.montant_reel ?? null);
        if (hasMontantReelChange) {
          await axios.put(`${API}/cash-closures/${selectedEntry.id}/validate`, {
            montant_reel: parseFloat(newMontantReel),
            notes: wizardData.notes || '',
          }, { headers });
        }

        toast.success('Entrée de caisse modifiée avec succès!');
      } else {
        // Mode création
        await axios.post(`${API}/cash-closures`, payload, { headers });
        toast.success('Entrée de caisse enregistrée!');
      }
      
      setCashWizardOpen(false);
      setSelectedEntry(null);
      fetchData();
      
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      if (error.response?.status === 403) {
        console.warn("Accès refusé. Droits administrateur requis.");
      } else if (error.response?.data?.detail) {
        toast.error(`Erreur: ${error.response.data.detail}`);
      } else {
        toast.error("Erreur lors de l'enregistrement de l'entrée");
      }
    }
  };

  const handleAdminUpdate = async (e) => {
    e.preventDefault();
    if (!selectedEntry) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      const response = await axios.put(`${API}/cash-closures/${selectedEntry.id}/validate`, {
        montant_reel: parseFloat(adminFormData.montant_reel),
        notes: adminFormData.notes
      }, { headers });
      
      const difference = response.data.difference;
      
      toast.success(
        difference === 0 
          ? 'Caisse équilibrée !' 
          : difference > 0 
          ? `Excédent de ${formatCurrency(difference)}` 
          : `Manque de ${formatCurrency(Math.abs(difference))}`
      );
      
      setAdminUpdateDialogOpen(false);
      setSelectedEntry(null);
      setAdminFormData({ montant_reel: '', notes: '' });
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        console.warn('Accès refusé. Droits administrateur requis.');
      } else {
        toast.error('Erreur lors de la validation');
      }
    }
  };

  const openAdminDialog = (entry) => {
    setSelectedEntry(entry);
    setAdminFormData({
      montant_reel: entry.montant_reel?.toString() || '',
      notes: entry.admin_notes || '',
    });
    setAdminUpdateDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce revenu?')) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      await axios.delete(`${API}/revenues/${id}`, { headers });
      toast.success('Revenu supprimé avec succès');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteCA = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée CA?')) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      await axios.delete(`${API}/revenue-entries/${id}`, { headers });
      toast.success('Entrée CA supprimée avec succès');
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        console.warn('Accès refusé. Droits administrateur requis.');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleDeleteCashEntry = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée de caisse?')) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      await axios.delete(`${API}/cash-closures/${id}`, { headers });
      toast.success('Entrée de caisse supprimée avec succès');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditCashEntry = (entry) => {
    setSelectedEntry(entry);
    setCashWizardOpen(true); // Ouvrir le wizard au lieu du dialog
  };


  const handleEditCA = (entry) => {
    setEditingCA(entry);
    setWizardOpen(true); // Ouvrir le wizard au lieu du dialog
  };

  const handleEditChifa = (revenue) => {
    setEditingChifa(revenue);
    setChifaWizardOpen(true); // Ouvrir le wizard au lieu du dialog
  };

  const resetCAForm = () => {
    setEditingCA(null);
    setCAFormData({
      date_start: format(new Date(), 'yyyy-MM-dd'),
      date_end: format(new Date(), 'yyyy-MM-dd'),
      amount: '',
      type: 'chiffre_affaires',
      notes: '',
    });
  };

  const resetChifaForm = () => {
    setEditingChifa(null);
    setChifaFormData({
      date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      amount: '',
      description: '',
      reference: '',
      category: 'CNAS',
    });
  };


  const formatNumberInput = (value) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', { 
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' DA';
  };

  const formatPeriod = (entry) => {
    const start = new Date(entry.date_start);
    const end = new Date(entry.date_end);
    
    if (entry.period_type === 'daily') {
      return format(start, 'dd/MM/yyyy', { locale: fr });
    } else if (entry.period_type === 'weekly') {
      return `${format(start, 'dd/MM', { locale: fr })} → ${format(end, 'dd/MM/yyyy', { locale: fr })}`;
    } else {
      // Pour les périodes mensuelles, afficher la plage complète au lieu de juste le mois
      return `${format(start, 'dd MMM', { locale: fr })} → ${format(end, 'dd MMM yyyy', { locale: fr })}`;
    }
  };

  // Calculs des KPIs DYNAMIQUES basés sur le mois en cours
  const calculateMonthlyKPIs = () => {
    if (!revenueEntries || revenueEntries.length === 0) {
      return {
        totalCA: 0,
        totalRevenuEspece: 0,
        totalRevenuCaisse: 0,
        totalBeneficeTheorique: 0,
        totalBeneficeNet: 0,
        totalTransactions: 0
      };
    }

    // Nouvelle structure : regrouper par type
    return {
      totalCA: revenueEntries
        .filter(entry => entry.type === 'chiffre_affaires')
        .reduce((sum, entry) => sum + (entry.amount || 0), 0),
      totalRevenuEspece: revenueEntries
        .filter(entry => entry.type === 'revenu_espece')
        .reduce((sum, entry) => sum + (entry.amount || 0), 0),
      totalRevenuCaisse: revenueEntries
        .filter(entry => entry.type === 'revenu_caisse')
        .reduce((sum, entry) => sum + (entry.amount || 0), 0),
      totalBeneficeTheorique: revenueEntries
        .filter(entry => entry.type === 'benefice_theorique')
        .reduce((sum, entry) => sum + (entry.amount || 0), 0),
      totalBeneficeNet: revenueEntries
        .filter(entry => entry.type === 'benefice_net')
        .reduce((sum, entry) => sum + (entry.amount || 0), 0),
      totalTransactions: revenueEntries.length
    };
  };

  const monthlyKPIs = calculateMonthlyKPIs();

  // ===== DONUT "Répartition des revenus" — dynamic, pharmacy-scoped =====
  const donutData = useMemo(() => {
    const totalCA      = (revenueEntries || []).filter(e => e.type === 'chiffre_affaires').reduce((s, e) => s + (e.amount || 0), 0);
    const cashFlow     = (cashClosures || []).reduce((s, e) => s + (e.montant_caissier || 0), 0);
    const credits      = (revenueEntries || []).filter(e => e.type === 'credits' || e.type === 'credit').reduce((s, e) => s + (e.amount || 0), 0);
    // "Autres" = pharmacy-scoped revenues that aren't already counted in CA / cash / credits
    const autres       = (revenues || []).reduce((s, r) => s + (r.amount || 0), 0);

    const total = totalCA + cashFlow + credits + autres;
    if (total === 0) {
      return { items: [], total: 0, dominant: null };
    }

    const items = [
      { name: "Chiffre d'Affaires", amount: totalCA,  color: '#10b981' },
      { name: 'Cash flow restant',  amount: cashFlow, color: '#3b82f6' },
      { name: 'Crédits',             amount: credits,  color: '#a855f7' },
      { name: 'Autres',              amount: autres,   color: '#e5e7eb' },
    ]
      .filter(s => s.amount > 0)
      .map(s => ({ ...s, value: +(s.amount / total * 100).toFixed(1) }));

    const dominant = items.length ? items.reduce((a, b) => (b.amount > a.amount ? b : a)) : null;
    return { items, total, dominant };
  }, [revenueEntries, cashClosures, revenues]);


  // ===== ÉVOLUTION CHART DATA — aggregation by selected period =====
  const evolutionChartData = useMemo(() => {
    if (!revenueEntries || revenueEntries.length === 0) return [];

    // Bucket key + label per period
    const bucketKey = (d) => {
      const dt = startOfDay(new Date(d));
      if (evolutionPeriod === 'day') {
        return { key: format(dt, 'yyyy-MM-dd'), label: format(dt, 'dd/MM', { locale: fr }), sortKey: dt.getTime() };
      }
      if (evolutionPeriod === 'week') {
        const weekStart = startOfWeek(dt, { weekStartsOn: 6 }); // pharmacy week starts Saturday
        const wkNum = getISOWeek(weekStart);
        return {
          key: format(weekStart, 'yyyy-MM-dd'),
          label: `S${wkNum} ${format(weekStart, 'MMM', { locale: fr })}`,
          sortKey: weekStart.getTime(),
        };
      }
      // month
      const mStart = startOfMonth(dt);
      return {
        key: format(mStart, 'yyyy-MM'),
        label: format(mStart, 'MMM yy', { locale: fr }),
        sortKey: mStart.getTime(),
      };
    };

    const buckets = new Map();
    for (const entry of revenueEntries) {
      const refDate = entry.date_start || entry.date;
      if (!refDate) continue;
      const { key, label, sortKey } = bucketKey(refDate);
      if (!buckets.has(key)) {
        buckets.set(key, { date: label, ca: 0, cash: 0, autres: 0, _sortKey: sortKey });
      }
      const slot = buckets.get(key);
      const amt = entry.amount || 0;
      if (entry.type === 'chiffre_affaires') slot.ca += amt;
      else if (entry.type === 'revenu_espece' || entry.type === 'revenu_caisse') slot.cash += amt;
      else slot.autres += amt;
    }

    // Add cashClosures (montant_caissier) into the 'cash' bucket
    for (const ce of cashClosures || []) {
      const refDate = ce.date;
      if (!refDate) continue;
      const { key, label, sortKey } = bucketKey(refDate);
      if (!buckets.has(key)) {
        buckets.set(key, { date: label, ca: 0, cash: 0, autres: 0, _sortKey: sortKey });
      }
      buckets.get(key).cash += ce.montant_caissier || 0;
    }

    return Array.from(buckets.values())
      .sort((a, b) => a._sortKey - b._sortKey)
      .map(({ _sortKey, ...rest }) => rest);
  }, [revenueEntries, cashClosures, evolutionPeriod]);

  
  const chifaRevenues = revenues.filter(r => r.type === 'chifa');
  // Total Sécurité Sociale = Total encaissé (somme des Montants Payés / virement_amount)
  // Identique à la formule utilisée sur la page Sécurité Sociale et dans la carte "Total encaissé"
  const totalSecuriteSociale = chifaRevenues.reduce((sum, r) => {
    let val = r.virement_amount;
    if (val == null || val === '') {
      val = r.is_paid ? r.amount : 0;
    }
    return sum + (parseFloat(val) || 0);
  }, 0);
  
  // Cash flow restant = Total des montants saisis par les caissiers
  const totalCashEntries = cashClosures.reduce((sum, e) => sum + (e.montant_caissier || 0), 0);
  const totalReelCash = cashClosures.reduce((sum, e) => sum + (e.montant_reel || 0), 0);
  
  // Use monthly aggregated KPIs
  const revenuEspece = monthlyKPIs.totalRevenuEspece; // Utilise ca_entries avec type revenu_espece
  // CORRECTION : Revenus Caisse = Total de toutes les entrées de Clôture Caisse + entrées manuelles 'revenu_caisse'
  const revenuCaisse = totalCashEntries + (monthlyKPIs.totalRevenuCaisse || 0);
  
  // CORRECTION : Chiffre d'Affaires = Sécurité Sociale + Entrée cash par défaut, sauf si saisi manuellement
  const manualCA = monthlyKPIs.totalCA;
  const chiffreAffaires = manualCA > 0 ? manualCA : (totalSecuriteSociale + revenuCaisse);
  const beneficeTheorique = monthlyKPIs.totalBeneficeTheorique;
  const beneficeNet = monthlyKPIs.totalBeneficeNet;

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'comptable';
  const isCaissier = currentUser?.role === 'caissier';

  // Filtrer les données selon les permissions view_all_users
  const filteredCAEntries = canViewAllUsers('revenue_entries') 
    ? revenueEntries 
    : revenueEntries.filter(entry => entry.created_by === currentUser?.id);

  const filteredCashEntries = canViewAllUsers('caisse.entrees')
    ? cashClosures
    : cashClosures.filter(entry => entry.user_id === currentUser?.id);

  const [cashSortConfig, setCashSortConfig] = useState({ key: 'date', direction: 'desc' });
  const handleCashSort = (key) => {
    setCashSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const sortedCashEntries = useMemo(() => {
    let sortableItems = [...filteredCashEntries];
    if (cashSortConfig !== null) {
      sortableItems.sort((a, b) => {
        let valA = 0, valB = 0;
        if (cashSortConfig.key === 'date') {
          valA = a.date ? parseSafeDate(a.date).getTime() : 0;
          valB = b.date ? parseSafeDate(b.date).getTime() : 0;
        } else if (cashSortConfig.key === 'pharmacy') {
          valA = (a.pharmacy_name || '').toLowerCase();
          valB = (b.pharmacy_name || '').toLowerCase();
        } else if (cashSortConfig.key === 'caissier') {
          valA = (a.username || a.user_fullname || '').toLowerCase();
          valB = (b.username || b.user_fullname || '').toLowerCase();
        } else if (cashSortConfig.key === 'utilisateur') {
          valA = (a.user_fullname || a.username || '').toLowerCase();
          valB = (b.user_fullname || b.username || '').toLowerCase();
        } else if (cashSortConfig.key === 'mt_caissier') {
          valA = a.montant_caissier || 0;
          valB = b.montant_caissier || 0;
        } else if (cashSortConfig.key === 'mt_valide') {
          valA = a.montant_reel || 0;
          valB = b.montant_reel || 0;
        } else if (cashSortConfig.key === 'difference') {
          valA = a.status === 'validated' ? (a.montant_reel || 0) - (a.montant_caissier || 0) : -999999999;
          valB = b.status === 'validated' ? (b.montant_reel || 0) - (b.montant_caissier || 0) : -999999999;
        } else if (cashSortConfig.key === 'status') {
          valA = a.status || '';
          valB = b.status || '';
        }
        if (valA < valB) return cashSortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return cashSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredCashEntries, cashSortConfig]);

  const SortableCashHeader = ({ label, sortKey, className = "" }) => {
    const isSorted = cashSortConfig.key === sortKey;
    return (
      <TableHead
        onClick={() => handleCashSort(sortKey)}
        className={`cursor-pointer select-none hover:text-gray-900 transition-colors ${className}`}
      >
        <div className="inline-flex items-center gap-1">
          <span>{label}</span>
          {isSorted ? (
            cashSortConfig.direction === 'desc' ? (
              <ArrowDown className="w-3.5 h-3.5 text-blue-600 font-bold" />
            ) : (
              <ArrowUp className="w-3.5 h-3.5 text-blue-600 font-bold" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-gray-300 hover:text-gray-500" />
          )}
        </div>
      </TableHead>
    );
  };

  const totalCashPages = Math.ceil(sortedCashEntries.length / cashItemsPerPage);
  const currentCashEntries = sortedCashEntries.slice((cashCurrentPage - 1) * cashItemsPerPage, cashCurrentPage * cashItemsPerPage);

  const filteredChifaRevenues = canViewAllUsers('securite_sociale') || canViewAllUsers('revenue_entries')
    ? chifaRevenues
    : chifaRevenues.filter(revenue => revenue.created_by === currentUser?.id);

  // === Helpers identiques à la page Sécurité Sociale ===
  const matchCategory = (paymentCategory, targetCategory) => {
    if (targetCategory === 'all') return true;
    if (!paymentCategory) return false;
    const cat = String(paymentCategory).trim();
    if (targetCategory === 'CNAS') return cat === 'CNAS';
    if (targetCategory === 'NON CNAS') return cat === 'NON CNAS' || cat === 'HORS CNAS';
    if (targetCategory === 'CASNOS') return cat === 'CASNOS';
    if (targetCategory === 'NON CASNOS') return cat === 'NON CASNOS' || cat === 'HORS CASNOS';
    if (targetCategory === 'Caisse militaire') return cat === 'Caisse militaire' || cat.toLowerCase().includes('militaire');
    return cat === targetCategory;
  };

  const sumByCategory = (list, category, field = 'virement_amount') => {
    return list
      .filter(p => matchCategory(p.category || p.chifa_category, category))
      .reduce((s, p) => {
        let val = p[field];
        if (field === 'virement_amount' && (val == null || val === '')) {
          val = p.is_paid ? p.amount : 0;
        }
        return s + (parseFloat(val) || 0);
      }, 0);
  };
  const countByCategory = (list, category) => {
    return list.filter(p => matchCategory(p.category || p.chifa_category, category)).length;
  };
  const ssTotalEncaisse = sumByCategory(filteredChifaRevenues, 'all', 'virement_amount');
  const ssChifa = sumByCategory(filteredChifaRevenues, 'CNAS', 'virement_amount');
  const ssHorsChifa = sumByCategory(filteredChifaRevenues, 'NON CNAS', 'virement_amount');
  const ssCasnos = sumByCategory(filteredChifaRevenues, 'CASNOS', 'virement_amount');
  const ssNonCasnos = sumByCategory(filteredChifaRevenues, 'NON CASNOS', 'virement_amount');
  const ssMilitaire = sumByCategory(filteredChifaRevenues, 'Caisse militaire', 'virement_amount');

  // Toggle paid status (identique à SecuriteSociale)
  const togglePaidStatus = async (payment) => {
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      const newStatus = !payment.is_paid;
      await axios.patch(`${API}/revenues/${payment.id}/paid-status`, { is_paid: newStatus }, { headers });
      toast.success(newStatus ? 'Marqué comme Payé' : 'Marqué comme Non payé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const typeLabels = {
    chiffre_affaires: 'Chiffre d\'Affaires',
    revenu_espece: 'Cash flow restant',
    revenu_caisse: 'Entrée cash',
    benefice_theorique: 'Bénéfice Théorique',
    benefice_net: 'Bénéfice Net',
  };

  const [tableFilteredCA, setTableFilteredCA] = useState(null);
  const caFilterConfig = [
    { key: 'pharmacy_name', accessor: (r) => r.pharmacy_name || '—' },
    { key: 'type_label', accessor: (r) => typeLabels[r.type] || r.type || '—' },
    { key: 'created_by_name', accessor: (r) => r.created_by_fullname || r.created_by_username || '—' },
  ];
  const { filters: caFilters, setFilter: setCaFilter, getUniqueValues: caUniqueValues, filteredData: caFilteredData } = useTableFilters(filteredCAEntries, caFilterConfig);

  const [caSortConfig, setCaSortConfig] = useState({ key: 'date', direction: 'desc' });

  const handleCaSort = (key) => {
    setCaSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const getSortedCaEntries = (entries) => {
    if (!entries || entries.length === 0) return entries;
    const sorted = [...entries];
    const { key, direction } = caSortConfig;
    const mult = direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      let valA = 0;
      let valB = 0;

      if (key === 'date') {
        const dateA = a.date_start || a.date;
        const dateB = b.date_start || b.date;
        valA = dateA ? new Date(dateA).getTime() : 0;
        valB = dateB ? new Date(dateB).getTime() : 0;
      } else if (key === 'amount') {
        valA = parseFloat(a.amount) || 0;
        valB = parseFloat(b.amount) || 0;
      }

      if (valA < valB) return -1 * mult;
      if (valA > valB) return 1 * mult;
      return 0;
    });

    return sorted;
  };

  const ssFilterConfig = [
    { key: 'pharmacy_name', accessor: (r) => r.pharmacy_name || '—' },
    { key: 'category', accessor: (r) => (r.category || r.chifa_category || 'CNAS') === 'NON CNAS' ? 'HORS CNAS' : (r.category || r.chifa_category) === 'NON CASNOS' ? 'HORS CASNOS' : (r.category || r.chifa_category || 'CNAS') },
    { key: 'created_by_name', accessor: (r) => r.created_by_fullname || r.created_by_username || '—' },
    { key: 'is_paid', accessor: (r) => r.is_paid ? 'Payé' : 'Non payé' },
  ];
  const { filters: ssFilters, setFilter: setSsFilter, getUniqueValues: ssUniqueValues, filteredData: ssFilteredData } = useTableFilters(filteredChifaRevenues, ssFilterConfig);

  const [chifaSortConfig, setChifaSortConfig] = useState({ key: 'date', direction: 'desc' });

  const handleChifaSort = (key) => {
    setChifaSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const sortedChifaRevenues = useMemo(() => {
    if (!ssFilteredData || ssFilteredData.length === 0) return ssFilteredData;
    const sorted = [...ssFilteredData];
    const { key, direction } = chifaSortConfig;
    const mult = direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      let valA = 0;
      let valB = 0;

      if (['date', 'liquidation_date', 'validation_date', 'virement_date', 'encaissement_date'].includes(key)) {
        const dateA = a[key];
        const dateB = b[key];
        valA = dateA ? new Date(dateA).getTime() : 0;
        valB = dateB ? new Date(dateB).getTime() : 0;
      } else if (['amount', 'montant_officine', 'virement_amount', 'montant_rejete'].includes(key)) {
        valA = parseFloat(a[key]) || 0;
        valB = parseFloat(b[key]) || 0;
      }

      if (valA < valB) return -1 * mult;
      if (valA > valB) return 1 * mult;
      return 0;
    });

    return sorted;
  }, [ssFilteredData, chifaSortConfig]);

  const CA_ENTRIES = getSortedCaEntries(caFilteredData.filter(e => e.type === 'chiffre_affaires'));
  const REVENU_ESPECE_ENTRIES = getSortedCaEntries(caFilteredData.filter(e => e.type === 'revenu_espece'));
  const CHIFA_ENTRIES = sortedChifaRevenues;



  return (
    <div className="space-y-4 sm:space-y-6 fade-in bg-gray-50 min-h-screen p-3 sm:p-6" data-testid="revenues-page">
      {/* HEADER */}
      <div className="mb-4 sm:mb-6">
        {/* Titre et Avatar */}
        <div className="flex items-start justify-between mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Revenus</h1>

        </div>
        
        {/* GRAND BLOC BLANC AVEC FILTRES */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 min-w-0">
              {currentUser?.role === 'super_admin' && (
                <ExpensePharmacyFilter
                  selectedPharmacies={superAdminPharmacyFilter}
                  onChange={setSuperAdminPharmacyFilter}
                  currentUser={currentUser}
                />
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {canFilterPeriod('revenues') && (
                <ExpenseDateRangePicker
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                />
              )}
              {/* Bouton dynamique selon onglet actif */}
              {canCreate('revenues') && (() => {
                const cfg = activeTab === 'caisses'
                  ? { label: 'Ajouter une entrée', color: 'blue', onClick: () => { setSelectedEntry(null); setCashWizardOpen(true); }, testid: 'add-revenue-top-btn' }
                  : activeTab === 'chifa'
                  ? { label: 'Ajouter une entrée', color: 'purple', onClick: () => { setEditingChifa(null); setChifaWizardOpen(true); }, testid: 'add-revenue-top-btn' }
                  : { label: 'Ajouter une entrée', color: 'green', onClick: () => { setEditingCA(null); setWizardOpen(true); }, testid: 'add-revenue-top-btn' };
                const colorMap = {
                  green: 'text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300',
                  blue: 'text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300',
                  purple: 'text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300',
                };
                return (
                  <button
                    onClick={cfg.onClick}
                    data-testid={cfg.testid}
                    className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 bg-white shadow-sm ${colorMap[cfg.color]}`}
                  >
                    <Plus className="w-5 h-5" />
                    {cfg.label}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* 6 CARTES KPI */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3 sm:gap-6 mb-4 sm:mb-6">
          {/* Carte 1: Chiffre d'Affaires */}
          <Card className="stat-card border-l-4 border-l-emerald-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <CardTitle className="text-sm font-medium text-gray-600">Chiffre d'Affaires</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">{chiffreAffaires.toLocaleString('fr-DZ')} DA</div>
              <div className="flex items-center gap-1 text-xs mt-1">
                <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" />
                <span className="font-semibold text-emerald-600">+18,5%</span>
                <span className="text-gray-500">vs mois dernier</span>
              </div>
              {/* Mini graphique */}
              <div className="mt-2 h-8">
                <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height="100%">
                  <LineChart data={[
                    { val: 400 }, { val: 500 }, { val: 450 }, { val: 600 }, { val: 550 }, { val: 700 }, { val: 800 }
                  ]}>
                    <Line type="monotone" dataKey="val" stroke="#10b981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Carte 2: Sécurité Sociale */}
          <Card className="stat-card border-l-4 border-l-pink-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-pink-600" />
                </div>
                <CardTitle className="text-sm font-medium text-gray-600">Sécurité Sociale</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-pink-600">{totalSecuriteSociale.toLocaleString('fr-DZ')} DA</div>
              <div className="flex items-center gap-1 text-xs mt-1">
                <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" />
                <span className="font-semibold text-emerald-600">+15,2%</span>
                <span className="text-gray-500">vs mois dernier</span>
              </div>
              {/* Mini graphique */}
              <div className="mt-2 h-8">
                <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height="100%">
                  <LineChart data={[
                    { val: 200 }, { val: 250 }, { val: 300 }, { val: 350 }, { val: 400 }, { val: 450 }, { val: 500 }
                  ]}>
                    <Line type="monotone" dataKey="val" stroke="#ec4899" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Carte 3: Revenu Caisse */}
          <Card className="stat-card border-l-4 border-l-purple-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <CardTitle className="text-sm font-medium text-gray-600">Entrée cash</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{revenuCaisse.toLocaleString('fr-DZ')} DA</div>
              <div className="flex items-center gap-1 text-xs mt-1">
                <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" />
                <span className="font-semibold text-emerald-600">+21,3%</span>
                <span className="text-gray-500">vs mois dernier</span>
              </div>
              {/* Mini graphique */}
              <div className="mt-2 h-8">
                <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height="100%">
                  <LineChart data={[
                    { val: 250 }, { val: 350 }, { val: 450 }, { val: 500 }, { val: 600 }, { val: 700 }, { val: 730 }
                  ]}>
                    <Line type="monotone" dataKey="val" stroke="#a855f7" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>


          {/* Carte 5: Bénéfice Théorique */}
          <Card className="stat-card border-l-4 border-l-teal-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-teal-600" />
                </div>
                <CardTitle className="text-sm font-medium text-gray-600">Bénéfice Théorique</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-teal-600">{beneficeTheorique.toLocaleString('fr-DZ')} DA</div>
              <div className="flex items-center gap-1 text-xs mt-1">
                <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" />
                <span className="font-semibold text-emerald-600">+15,8%</span>
                <span className="text-gray-500">vs mois dernier</span>
              </div>
              {/* Mini graphique */}
              <div className="mt-2 h-8">
                <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height="100%">
                  <LineChart data={[
                    { val: 400 }, { val: 420 }, { val: 450 }, { val: 480 }, { val: 510 }, { val: 540 }, { val: 580 }
                  ]}>
                    <Line type="monotone" dataKey="val" stroke="#14b8a6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

        </div>

      {/* SECTION ANALYTICS - 2 colonnes (Donut + Bar Chart Empilé) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Colonne 1: Répartition des revenus (Donut Chart) */}
        <Card className="border-0 shadow-lg rounded-2xl bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900">Répartition des revenus</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              {/* Donut Chart */}
              <div className="flex-shrink-0" style={{ width: '200px', height: '200px' }} data-testid="revenue-donut">
                {donutData.total === 0 ? (
                  <div className="w-full h-full flex items-center justify-center text-sm text-gray-400 text-center px-4" data-testid="revenue-donut-empty">
                    Aucune donnée disponible
                  </div>
                ) : (
                <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData.items}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {donutData.items.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(_v, _n, props) => {
                        const it = props && props.payload;
                        if (!it) return ['', ''];
                        return [`${(it.amount || 0).toLocaleString('fr-DZ')} DA`, `${it.name} • ${it.value}%`];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                )}
                {/* Centre du donut — dynamique */}
                {donutData.total > 0 && donutData.dominant && (
                  <div className="relative -mt-32 text-center pointer-events-none" data-testid="revenue-donut-center">
                    <div className="text-sm text-gray-500">{donutData.dominant.name}</div>
                    <div className="text-2xl font-bold text-gray-900">{donutData.dominant.value}%</div>
                    <div className="text-sm text-gray-500">{donutData.total.toLocaleString('fr-DZ')} DA</div>
                  </div>
                )}
              </div>
              
              {/* Légende — dynamique */}
              <div className="flex-1 space-y-3" data-testid="revenue-donut-legend">
                {donutData.total === 0 ? (
                  <div className="text-sm text-gray-400">Pas de données pour la période sélectionnée.</div>
                ) : (
                  <div className="space-y-2">
                    {donutData.items.map((s, i) => (
                      <div key={i} className="flex items-center justify-between" data-testid={`revenue-donut-item-${i}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }}></div>
                          <span className="text-sm text-gray-700">{s.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-gray-900">{s.value.toString().replace('.', ',')}%</span>
                          <p className="text-xs text-gray-500">{s.amount.toLocaleString('fr-DZ')} DA</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Colonne 2: Évolution (Bar Chart Empilé) */}
        <Card className="border-0 shadow-lg rounded-2xl bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900">Évolution</CardTitle>
              <div className="flex gap-4 text-sm">
                <button
                  type="button"
                  onClick={() => setEvolutionPeriod('day')}
                  data-testid="evolution-period-day"
                  className={evolutionPeriod === 'day' ? 'text-emerald-600 font-semibold border-b-2 border-emerald-600' : 'text-gray-400 hover:text-gray-700'}
                >Jour</button>
                <button
                  type="button"
                  onClick={() => setEvolutionPeriod('week')}
                  data-testid="evolution-period-week"
                  className={evolutionPeriod === 'week' ? 'text-emerald-600 font-semibold border-b-2 border-emerald-600' : 'text-gray-400 hover:text-gray-700'}
                >Semaine</button>
                <button
                  type="button"
                  onClick={() => setEvolutionPeriod('month')}
                  data-testid="evolution-period-month"
                  className={evolutionPeriod === 'month' ? 'text-emerald-600 font-semibold border-b-2 border-emerald-600' : 'text-gray-400 hover:text-gray-700'}
                >Mois</button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {evolutionChartData.length === 0 ? (
              <div className="flex items-center justify-center text-sm text-gray-400" style={{ height: 250 }} data-testid="evolution-empty">
                Aucune donnée pour la période sélectionnée
              </div>
            ) : (
            <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height={250}>
              <BarChart
                data={evolutionChartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value) => `${value.toLocaleString('fr-DZ')} DA`}
                />
                <Legend />
                <Bar dataKey="ca" stackId="a" fill="#10b981" name="Chiffre d'Affaires" radius={[0, 0, 0, 0]} />
                <Bar dataKey="cash" stackId="a" fill="#3b82f6" name="Cash flow restant" radius={[0, 0, 0, 0]} />
                <Bar dataKey="autres" stackId="a" fill="#a855f7" name="Autres" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            )}
            <div className="flex items-center justify-end mt-4">
              <Button variant="outline" size="sm" className="text-sm">
                Tout afficher
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Anciennes cartes supprimées */}

      {/* TABS - 3 ONGLETS - ACTIF PLUS LARGE */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-6">
        <TabsList className="bg-transparent border-0 rounded-none p-0 h-auto mb-8 w-full flex gap-4">
          <TabsTrigger 
            value="ca" 
            className="bg-gray-50 data-[state=active]:bg-white rounded-xl shadow-sm data-[state=active]:shadow-lg px-12 py-6 text-gray-500 data-[state=active]:text-gray-900 font-normal data-[state=active]:font-semibold text-base border-0 transition-all data-[state=active]:flex-1"
          >
            Chiffre d'Affaires
          </TabsTrigger>
          <TabsTrigger 
            value="caisses"
            className="bg-gray-50 data-[state=active]:bg-white rounded-xl shadow-sm data-[state=active]:shadow-lg px-12 py-6 text-gray-500 data-[state=active]:text-gray-900 font-normal data-[state=active]:font-semibold text-base border-0 transition-all data-[state=active]:flex-1"
          >
            Entrée cash
          </TabsTrigger>
          <TabsTrigger 
            value="chifa"
            className="bg-gray-50 data-[state=active]:bg-white rounded-xl shadow-sm data-[state=active]:shadow-lg px-12 py-6 text-gray-500 data-[state=active]:text-gray-900 font-normal data-[state=active]:font-semibold text-base border-0 transition-all data-[state=active]:flex-1"
          >
            Sécurité Sociale
          </TabsTrigger>
          <TabsTrigger 
            value="revenu_espece"
            className="bg-gray-50 data-[state=active]:bg-white rounded-xl shadow-sm data-[state=active]:shadow-lg px-12 py-6 text-gray-500 data-[state=active]:text-gray-900 font-normal data-[state=active]:font-semibold text-base border-0 transition-all data-[state=active]:flex-1"
          >
            Cash flow restant
          </TabsTrigger>
        </TabsList>

        {/* Onglet Chiffre d'Affaires */}
        <TabsContent value="ca" className="space-y-4 mt-6">

          {/* Ancien modal (gardé pour l'instant mais caché) */}
              <Dialog open={caDialogOpen} onOpenChange={(open) => {
                setCADialogOpen(open);
                if (!open) resetCAForm();
              }} style={{display: 'none'}}>
                <DialogTrigger asChild style={{display: 'none'}}>
                  <Button className="bg-gradient-to-r from-[#004d3d] to-[#00ff7f] hover:from-green-700 hover:to-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter Entrée CA (Ancien)
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-light">{editingCA ? 'Modifier l\'Entrée CA' : 'Nouvelle Entrée Chiffre d\'Affaires'}</DialogTitle>
                    <DialogDescription className="text-gray-500">
                      {editingCA ? 'Modifiez les données financières' : 'Enregistrez les données financières pour une période'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCASubmit} className="space-y-6">
                    {/* Sélection de période avec calendrier moderne */}
                    <div>
                      <Label className="text-gray-700 font-medium">Période *</Label>
                      <div className="mt-2">
                        <ModernDateRangePicker 
                          dateRange={{
                            from: caFormData.date_start ? new Date(caFormData.date_start) : undefined,
                            to: caFormData.date_end ? new Date(caFormData.date_end) : undefined
                          }}
                          onDateRangeChange={(range) => {
                            setCAFormData({
                              ...caFormData,
                              date_start: range.from ? format(range.from, 'yyyy-MM-dd') : '',
                              date_end: range.to ? format(range.to, 'yyyy-MM-dd') : ''
                            });
                          }}
                        />
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-6">
                      <h4 className="font-medium text-gray-800 mb-4">Données Financières</h4>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="amount" className="text-gray-700 font-medium">Montant (DA) *</Label>
                          <Input
                            id="amount"
                            type="text"
                            value={caFormData.amount ? formatNumberInput(caFormData.amount) : ''}
                            onChange={(e) => {
                              const rawValue = e.target.value.replace(/\s/g, '').replace(',', '.');
                              if (!isNaN(rawValue) || rawValue === '') {
                                setCAFormData({ ...caFormData, amount: rawValue });
                              }
                            }}
                            onBlur={(e) => {
                              const num = parseFloat(e.target.value.replace(/\s/g, '').replace(',', '.'));
                              if (!isNaN(num)) {
                                setCAFormData({ ...caFormData, amount: num.toString() });
                              }
                            }}
                            required
                            className="mt-2 py-6 text-lg"
                            placeholder="100 000,00"
                          />
                        </div>
                        <div>
                          <Label htmlFor="type" className="text-gray-700 font-medium">Type *</Label>
                          <Select 
                            value={caFormData.type} 
                            onValueChange={(value) => setCAFormData({ ...caFormData, type: value })} 
                            required
                          >
                            <SelectTrigger className="mt-2 py-6">
                              <SelectValue placeholder="Sélectionnez un type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="chiffre_affaires">Chiffre d'Affaires</SelectItem>
                              <SelectItem value="revenu_espece">Cash flow restant</SelectItem>
                              <SelectItem value="revenu_caisse">Entrée cash</SelectItem>
                              <SelectItem value="benefice_theorique">Bénéfice Théorique</SelectItem>
                              <SelectItem value="benefice_net">Bénéfice Net</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="notes_ca" className="text-gray-700 font-medium">Notes (Commentaire)</Label>
                      <Input
                        id="notes_ca"
                        type="text"
                        value={caFormData.notes}
                        onChange={(e) => setCAFormData({ ...caFormData, notes: e.target.value })}
                        className="mt-2 py-6"
                        placeholder="Ajoutez un commentaire..."
                      />
                    </div>

                    <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 rounded-xl shadow-lg">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      {editingCA ? 'Modifier' : 'Enregistrer'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

          {/* NOUVELLE TABLE MODERNE - Design identique à Dépenses */}
          {canViewHistory('revenues') ? (
          <Card className="border-0 shadow-lg rounded-2xl bg-white">

            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-gray-100 bg-gray-50">
                        <SortableHeader label="Date" sortKey="date" currentSort={caSortConfig} onSort={handleCaSort} />
                        <FilterableHeader label="Pharmacie" allLabel="TOUS" values={caUniqueValues['pharmacy_name'] || []} selected={caFilters.pharmacy_name} onSelect={(v) => setCaFilter('pharmacy_name', v)} className="hidden sm:table-cell" />
                        <SortableHeader label="Montant" sortKey="amount" currentSort={caSortConfig} onSort={handleCaSort} />
                        <FilterableHeader label="Utilisateur" allLabel="TOUS" values={caUniqueValues['created_by_name'] || []} selected={caFilters.created_by_name} onSelect={(v) => setCaFilter('created_by_name', v)} className="hidden md:table-cell" />
                        <TableHead className="text-xs font-semibold text-gray-600 uppercase text-right px-4 py-3">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {CA_ENTRIES.slice((caCurrentPage - 1) * itemsPerPage, caCurrentPage * itemsPerPage).map((entry, index) => (
                      <TableRow key={entry.id} className={`border-b border-gray-100 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <TableCell className="whitespace-nowrap text-gray-900 font-medium text-sm px-4 py-3">
                          {entry.date_start && entry.date_end ? (
                            <>
                              {format(new Date(entry.date_start), 'dd MMM', { locale: fr })} - {format(new Date(entry.date_end), 'dd MMM yyyy', { locale: fr })}
                            </>
                          ) : (
                            format(new Date(entry.date_start), 'dd MMM yyyy', { locale: fr })
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell px-4 py-3">
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 font-medium">
                            {entry.pharmacy_name}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-bold text-gray-900 px-4 py-3">{entry.amount.toLocaleString('fr-DZ')} DA</TableCell>
                        <TableCell className="hidden md:table-cell px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">
                              {(entry.created_by_fullname || entry.created_by_username || 'U').charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm text-gray-700">{entry.created_by_fullname || entry.created_by_username || 'Utilisateur'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {canUpdate('revenues') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditCA(entry)}
                                className="text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 h-8 w-8 p-0"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete('revenues') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCA(entry.id)}
                                className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination CA */}
              {CA_ENTRIES.length > 0 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">
                      {Math.min(CA_ENTRIES.length, (caCurrentPage - 1) * itemsPerPage + 1)} - {Math.min(CA_ENTRIES.length, caCurrentPage * itemsPerPage)}
                    </span> résultats sur <span className="font-medium">{CA_ENTRIES.length}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setCaCurrentPage(prev => Math.max(1, prev - 1))} disabled={caCurrentPage === 1} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600">
                      &lt;
                    </Button>
                    <span className="text-sm font-medium text-gray-700">
                      {caCurrentPage} / {Math.max(1, Math.ceil(CA_ENTRIES.length / itemsPerPage))}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setCaCurrentPage(prev => prev + 1)} disabled={caCurrentPage >= Math.ceil(CA_ENTRIES.length / itemsPerPage)} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600">
                      &gt;
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-sm text-yellow-700">Vous n'avez pas la permission de voir l'historique des revenus</p>
            </div>
          )}
        </TabsContent>

        {/* Onglet Cash flow restant */}
        <TabsContent value="revenu_espece" className="space-y-4">
          {canViewHistory('revenues') ? (
          <Card className="border-0 shadow-lg rounded-2xl bg-white">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-gray-100 bg-gray-50">
                        <SortableHeader label="Date" sortKey="date" currentSort={caSortConfig} onSort={handleCaSort} />
                        <FilterableHeader label="Pharmacie" allLabel="TOUS" values={caUniqueValues['pharmacy_name'] || []} selected={caFilters.pharmacy_name} onSelect={(v) => setCaFilter('pharmacy_name', v)} className="hidden sm:table-cell" />
                        <SortableHeader label="Montant" sortKey="amount" currentSort={caSortConfig} onSort={handleCaSort} />
                        <FilterableHeader label="Utilisateur" allLabel="TOUS" values={caUniqueValues['created_by_name'] || []} selected={caFilters.created_by_name} onSelect={(v) => setCaFilter('created_by_name', v)} className="hidden md:table-cell" />
                        <TableHead className="text-xs font-semibold text-gray-600 uppercase text-right px-4 py-3">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {REVENU_ESPECE_ENTRIES.slice((revenuEspeceCurrentPage - 1) * itemsPerPage, revenuEspeceCurrentPage * itemsPerPage).map((entry, index) => (
                      <TableRow key={entry.id} className={`border-b border-gray-100 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <TableCell className="whitespace-nowrap text-gray-900 font-medium text-sm px-4 py-3">
                          {entry.date_start && entry.date_end ? (
                            <>
                              {format(new Date(entry.date_start), 'dd MMM', { locale: fr })} - {format(new Date(entry.date_end), 'dd MMM yyyy', { locale: fr })}
                            </>
                          ) : (
                            format(new Date(entry.date_start), 'dd MMM yyyy', { locale: fr })
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell px-4 py-3">
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 font-medium">
                            {entry.pharmacy_name}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-bold text-gray-900 px-4 py-3">{entry.amount.toLocaleString('fr-DZ')} DA</TableCell>
                        <TableCell className="hidden md:table-cell px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">
                              {(entry.created_by_fullname || entry.created_by_username || 'U').charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm text-gray-700">{entry.created_by_fullname || entry.created_by_username || 'Utilisateur'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {canUpdate('revenues') && (
                              <Button variant="ghost" size="sm" onClick={() => handleEditCA(entry)} className="text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 h-8 w-8 p-0">
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete('revenues') && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteCA(entry.id)} className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination Revenu Espece */}
              {REVENU_ESPECE_ENTRIES.length > 0 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">
                      {Math.min(REVENU_ESPECE_ENTRIES.length, (revenuEspeceCurrentPage - 1) * itemsPerPage + 1)} - {Math.min(REVENU_ESPECE_ENTRIES.length, revenuEspeceCurrentPage * itemsPerPage)}
                    </span> résultats sur <span className="font-medium">{REVENU_ESPECE_ENTRIES.length}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setRevenuEspeceCurrentPage(prev => Math.max(1, prev - 1))} disabled={revenuEspeceCurrentPage === 1} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600">
                      &lt;
                    </Button>
                    <span className="text-sm font-medium text-gray-700">
                      {revenuEspeceCurrentPage} / {Math.max(1, Math.ceil(REVENU_ESPECE_ENTRIES.length / itemsPerPage))}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setRevenuEspeceCurrentPage(prev => prev + 1)} disabled={revenuEspeceCurrentPage >= Math.ceil(REVENU_ESPECE_ENTRIES.length / itemsPerPage)} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600">
                      &gt;
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-sm text-yellow-700">Vous n'avez pas la permission de voir l'historique des revenus</p>
            </div>
          )}
        </TabsContent>

        {/* Onglet Caisses */}
        <TabsContent value="caisses" className="space-y-4">

          {canViewHistory('caisse.entrees') ? (
          <Card>
            <CardHeader>
              <CardTitle>Entrées de Caisse par Utilisateur</CardTitle>
              <CardDescription>
                {isCaissier 
                  ? "📅 Vous voyez l'historique de vos entrées de caisse"
                  : 'Gestion des montants et validation'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isCaissier && cashClosures.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>Aucune entrée pour cette période</p>
                  <p className="text-sm mt-2">Cliquez sur "Saisir Mon Montant" pour enregistrer votre caisse</p>
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableCashHeader label="Date" sortKey="date" />
                      <SortableCashHeader label="Pharmacie" sortKey="pharmacy" />
                      {!isCaissier && <SortableCashHeader label="Caissier" sortKey="caissier" />}
                      <SortableCashHeader label="Montant Caissier" sortKey="mt_caissier" className="w-32" />
                      <SortableCashHeader label="Montant Réel" sortKey="mt_valide" className="w-32" />
                      <SortableCashHeader label="Différence" sortKey="difference" className="w-32" />
                      <SortableCashHeader label="Statut" sortKey="status" />
                      {!isCaissier && <SortableCashHeader label="Utilisateur" sortKey="utilisateur" />}
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentCashEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="whitespace-nowrap">
                          {format(parseSafeDate(entry.date), 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </TableCell>
                        <TableCell>
                          <Badge className="border bg-gray-50 text-gray-700 border-gray-200">
                            {entry.pharmacy_name || 'N/A'}
                          </Badge>
                        </TableCell>
                        {!isCaissier && (
                          <TableCell className="font-medium">{entry.user_fullname || entry.username}</TableCell>
                        )}
                        <TableCell className="font-bold text-blue-600 w-32">
                          {formatCurrency(entry.montant_caissier)}
                        </TableCell>
                        <TableCell className="font-bold text-purple-600 w-32">
                          {entry.montant_reel !== null ? formatCurrency(entry.montant_reel) : '-'}
                        </TableCell>
                        <TableCell className="w-32">
                          {entry.difference !== null ? (
                            <Badge 
                              variant="outline"
                              className={
                                entry.difference === 0
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : entry.difference > 0
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : 'bg-red-50 text-red-700 border-red-200'
                              }
                            >
                              {entry.difference === 0 ? '✓ OK' : formatCurrency(entry.difference)}
                            </Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={entry.status === 'validated' ? 'default' : 'secondary'}>
                            {entry.status === 'validated' ? 'Validé' : 'En attente'}
                          </Badge>
                        </TableCell>
                        {!isCaissier && (
                          <TableCell className="font-medium text-gray-700">
                            {entry.created_by_username || 'Système'}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Bouton VALIDER (Admin uniquement, si pending) */}
                            {canValidateCash() && isAdmin && entry.status === 'pending' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openAdminDialog(entry)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                Valider
                              </Button>
                            )}
                            {/* Bouton MODIFIER (Caissier ne peut pas modifier après validation) */}
                            {canUpdate('caisse.entrees') && !isCaissier && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditCashEntry(entry)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Bouton SUPPRIMER (tous utilisateurs avec permission delete) */}
                            {canDelete('caisse.entrees') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCashEntry(entry.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              {sortedCashEntries.length > 0 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">
                      {Math.min(sortedCashEntries.length, (cashCurrentPage - 1) * cashItemsPerPage + 1)} - {Math.min(sortedCashEntries.length, cashCurrentPage * cashItemsPerPage)}
                    </span> résultats sur <span className="font-medium">{sortedCashEntries.length}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setCashCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={cashCurrentPage === 1}
                      className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                    >
                      &lt;
                    </Button>
                    <span className="text-sm text-gray-600 px-2">Page {cashCurrentPage} / {totalCashPages || 1}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setCashCurrentPage(prev => Math.min(totalCashPages, prev + 1))}
                      disabled={cashCurrentPage === totalCashPages}
                      className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                    >
                      &gt;
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-sm text-yellow-700">Vous n'avez pas la permission de voir l'historique des caisses</p>
            </div>
          )}
        </TabsContent>

        {/* Onglet Sécurité Sociale */}
        <TabsContent value="chifa" className="space-y-4">
          <>

            {/* === KPI Cards (même formule que page Sécurité Sociale : virement_amount) === */}
            {canViewTotals('revenues') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                <Card className="stat-card border-l-4 border-l-green-500">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-green-500" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-600">Total encaissé</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(ssTotalEncaisse)}</div>
                    <p className="text-xs text-gray-400 mt-2">{filteredChifaRevenues.length} paiement{filteredChifaRevenues.length > 1 ? 's' : ''}</p>
                  </CardContent>
                </Card>

                <Card className="stat-card border-l-4 border-l-green-500">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-green-600" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-600">CNAS</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(ssChifa)}</div>
                    <p className="text-xs text-gray-400 mt-2">{countByCategory(filteredChifaRevenues, 'CNAS')} paiement{countByCategory(filteredChifaRevenues, 'CNAS') > 1 ? 's' : ''}</p>
                  </CardContent>
                </Card>

                <Card className="stat-card border-l-4 border-l-blue-500">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-600">HORS CNAS</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">{formatCurrency(ssHorsChifa)}</div>
                    <p className="text-xs text-gray-400 mt-2">{countByCategory(filteredChifaRevenues, 'NON CNAS')} paiement{countByCategory(filteredChifaRevenues, 'NON CNAS') > 1 ? 's' : ''}</p>
                  </CardContent>
                </Card>

                <Card className="stat-card border-l-4 border-l-orange-500">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                        <UsersIcon className="w-5 h-5 text-orange-600" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-600">CASNOS</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-600">{formatCurrency(ssCasnos)}</div>
                    <p className="text-xs text-gray-400 mt-2">{countByCategory(filteredChifaRevenues, 'CASNOS')} paiement{countByCategory(filteredChifaRevenues, 'CASNOS') > 1 ? 's' : ''}</p>
                  </CardContent>
                </Card>

                <Card className="stat-card border-l-4 border-l-purple-500">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-purple-600" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-600">HORS CASNOS</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">{formatCurrency(ssNonCasnos)}</div>
                    <p className="text-xs text-gray-400 mt-2">{countByCategory(filteredChifaRevenues, 'NON CASNOS')} paiement{countByCategory(filteredChifaRevenues, 'NON CASNOS') > 1 ? 's' : ''}</p>
                  </CardContent>
                </Card>

                <Card className="stat-card border-l-4 border-l-teal-500">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                        <Plus className="w-5 h-5 text-teal-600" />
                      </div>
                      <CardTitle className="text-sm font-medium text-gray-600">Caisse militaire</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-teal-600">{formatCurrency(ssMilitaire)}</div>
                    <p className="text-xs text-gray-400 mt-2">{countByCategory(filteredChifaRevenues, 'Caisse militaire')} paiement{countByCategory(filteredChifaRevenues, 'Caisse militaire') > 1 ? 's' : ''}</p>
                  </CardContent>
                </Card>
              </div>
            )}

          <Card>
            <CardHeader>
              <CardTitle>Historique des Paiements</CardTitle>
              <CardDescription>{filteredChifaRevenues.length} paiement(s) - Total encaissé: {formatCurrency(ssTotalEncaisse)}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : filteredChifaRevenues.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun paiement de sécurité sociale enregistré</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>N° Bordereau</TableHead>
                        <FilterableHeader label="Pharmacie" allLabel="TOUS" values={ssUniqueValues['pharmacy_name'] || []} selected={ssFilters.pharmacy_name} onSelect={(v) => setSsFilter('pharmacy_name', v)} />
                        <FilterableHeader label="Utilisateur" allLabel="TOUS" values={ssUniqueValues['created_by_name'] || []} selected={ssFilters.created_by_name} onSelect={(v) => setSsFilter('created_by_name', v)} />
                        <FilterableHeader label="Catégorie" allLabel="TOUTES" values={ssUniqueValues['category'] || []} selected={ssFilters.category} onSelect={(v) => setSsFilter('category', v)} formatValue={(v) => v === 'NON CNAS' ? 'HORS CNAS' : v === 'NON CASNOS' ? 'HORS CASNOS' : v} />
                        <SortableHeader label="Date dépôt" sortKey="date" currentSort={chifaSortConfig} onSort={handleChifaSort} />
                        <SortableHeader label="Date liquidation" sortKey="liquidation_date" currentSort={chifaSortConfig} onSort={handleChifaSort} />
                        <SortableHeader label="Date validation" sortKey="validation_date" currentSort={chifaSortConfig} onSort={handleChifaSort} />
                        <SortableHeader label="Date virement" sortKey="virement_date" currentSort={chifaSortConfig} onSort={handleChifaSort} />
                        <SortableHeader label="Montant bordereau" sortKey="amount" currentSort={chifaSortConfig} onSort={handleChifaSort} />
                        <SortableHeader label="Montant officine" sortKey="montant_officine" currentSort={chifaSortConfig} onSort={handleChifaSort} />
                        <SortableHeader label="Montant Payé" sortKey="virement_amount" currentSort={chifaSortConfig} onSort={handleChifaSort} />
                        <SortableHeader label="Montant rejeté" sortKey="montant_rejete" currentSort={chifaSortConfig} onSort={handleChifaSort} className="text-red-600" />
                        <SortableHeader label="Date encaissement" sortKey="encaissement_date" currentSort={chifaSortConfig} onSort={handleChifaSort} />
                        <FilterableHeader label="Statut" allLabel="TOUS" values={ssUniqueValues['is_paid'] || []} selected={ssFilters.is_paid} onSelect={(v) => setSsFilter('is_paid', v)} className="whitespace-nowrap" />
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                    {CHIFA_ENTRIES.slice((chifaCurrentPage - 1) * itemsPerPage, chifaCurrentPage * itemsPerPage).map((revenue) => (
                        <TableRow key={revenue.id} data-testid="chifa-row">
                          <TableCell className="font-medium text-gray-700">{revenue.bordereau_number || '-'}</TableCell>
                          <TableCell>
                            <Badge className="border bg-gray-50 text-gray-700 border-gray-200">{revenue.pharmacy_name || 'N/A'}</Badge>
                          </TableCell>
                          <TableCell>{revenue.created_by_username || 'Système'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              (revenue.category || revenue.chifa_category) === 'CNAS' ? 'bg-green-50 text-green-700 border-green-200'
                              : (revenue.category || revenue.chifa_category) === 'NON CNAS' || (revenue.category || revenue.chifa_category) === 'HORS CNAS' ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : (revenue.category || revenue.chifa_category) === 'CASNOS' ? 'bg-orange-50 text-orange-700 border-orange-200'
                              : (revenue.category || revenue.chifa_category) === 'NON CASNOS' || (revenue.category || revenue.chifa_category) === 'HORS CASNOS' ? 'bg-purple-50 text-purple-700 border-purple-200'
                              : 'bg-teal-50 text-teal-700 border-teal-200'
                            }>
                              {(revenue.category || revenue.chifa_category) === 'NON CNAS' ? 'HORS CNAS' :
                               (revenue.category || revenue.chifa_category) === 'NON CASNOS' ? 'HORS CASNOS' :
                               (revenue.category || revenue.chifa_category || 'CNAS')}
                            </Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{format(parseSafeDate(revenue.date), 'dd/MM/yyyy', { locale: fr })}</TableCell>
                          <TableCell className="whitespace-nowrap text-sm">{revenue.liquidation_date ? format(new Date(revenue.liquidation_date), 'dd/MM/yyyy', { locale: fr }) : <span className="text-gray-400 italic">—</span>}</TableCell>
                          <TableCell className="whitespace-nowrap text-sm">{revenue.validation_date ? format(new Date(revenue.validation_date), 'dd/MM/yyyy', { locale: fr }) : <span className="text-gray-400 italic">—</span>}</TableCell>
                          <TableCell className="whitespace-nowrap text-sm">{revenue.virement_date ? format(new Date(revenue.virement_date), 'dd/MM/yyyy', { locale: fr }) : <span className="text-gray-400 italic">—</span>}</TableCell>
                          <TableCell className="font-bold text-green-600 whitespace-nowrap">{formatCurrency(revenue.amount)}</TableCell>
                          <TableCell className="whitespace-nowrap font-semibold text-gray-800">{revenue.montant_officine != null ? formatCurrency(revenue.montant_officine) : <span className="text-gray-400 italic font-normal">—</span>}</TableCell>
                          <TableCell className="whitespace-nowrap font-semibold text-blue-700">{(revenue.virement_amount != null && revenue.virement_amount !== '') ? formatCurrency(revenue.virement_amount) : (revenue.is_paid ? formatCurrency(revenue.amount) : <span className="text-gray-400 italic font-normal">—</span>)}</TableCell>
                          <TableCell className="whitespace-nowrap font-semibold text-red-600">{revenue.montant_rejete != null ? formatCurrency(revenue.montant_rejete) : <span className="text-gray-400 italic font-normal text-gray-400">—</span>}</TableCell>
                          <TableCell className="whitespace-nowrap text-sm">{revenue.encaissement_date ? format(new Date(revenue.encaissement_date), 'dd/MM/yyyy', { locale: fr }) : <span className="text-gray-400 italic">—</span>}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {canUpdate('revenues') ? (
                              <button
                                type="button"
                                onClick={() => togglePaidStatus(revenue)}
                                data-testid="toggle-paid-btn"
                                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                                  revenue.is_paid ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'
                                }`}
                              >
                                {revenue.is_paid ? '✓ Payé' : '✕ Non payé'}
                              </button>
                            ) : (
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${revenue.is_paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {revenue.is_paid ? '✓ Payé' : '✕ Non payé'}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {canUpdate('revenues') && (
                                <Button variant="ghost" size="sm" onClick={() => handleEditChifa(revenue)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {canDelete('revenues') && (
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(revenue.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Pagination Chifa */}
              {CHIFA_ENTRIES.length > 0 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">
                      {Math.min(CHIFA_ENTRIES.length, (chifaCurrentPage - 1) * itemsPerPage + 1)} - {Math.min(CHIFA_ENTRIES.length, chifaCurrentPage * itemsPerPage)}
                    </span> résultats sur <span className="font-medium">{CHIFA_ENTRIES.length}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setChifaCurrentPage(prev => Math.max(1, prev - 1))} disabled={chifaCurrentPage === 1} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600">
                      &lt;
                    </Button>
                    <span className="text-sm font-medium text-gray-700">
                      {chifaCurrentPage} / {Math.max(1, Math.ceil(CHIFA_ENTRIES.length / itemsPerPage))}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setChifaCurrentPage(prev => prev + 1)} disabled={chifaCurrentPage >= Math.ceil(CHIFA_ENTRIES.length / itemsPerPage)} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600">
                      &gt;
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          </>
        </TabsContent>
      </Tabs>

      {/* Admin Update Dialog */}
      <Dialog open={adminUpdateDialogOpen} onOpenChange={setAdminUpdateDialogOpen}>
        <DialogContent className="sm:max-w-[425px] p-0 overflow-hidden bg-white/95 backdrop-blur-xl border-white/40 shadow-2xl">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <CheckCircle className="w-6 h-6" />
                Validation Caisse
              </h2>
              <p className="text-blue-100 mt-2 flex items-center gap-2 opacity-90">
                <UserCircle className="w-4 h-4" />
                Caissier: {selectedEntry?.username || 'Admin'}
              </p>
            </div>
            {/* abstract background circles */}
            <div className="absolute -right-4 -top-12 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute right-12 -bottom-8 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
          </div>

          <div className="p-6">
            <form onSubmit={handleAdminUpdate} className="space-y-5">
              <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">Montant Caissier</span>
                  <span className="text-lg font-bold text-gray-900">{formatCurrency(selectedEntry?.montant_caissier)}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="montant_reel" className="text-sm font-semibold text-gray-700">Montant Réel (DA) <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Wallet className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="montant_reel"
                    type="number"
                    step="0.01"
                    min="0"
                    value={adminFormData.montant_reel}
                    onChange={(e) => setAdminFormData({ ...adminFormData, montant_reel: e.target.value })}
                    required
                    className="pl-10 h-11 border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    placeholder="Saisissez le montant réel"
                  />
                </div>
              </div>

              {adminFormData.montant_reel && (
                <div className={`p-4 border rounded-xl transition-all duration-300 ${
                    selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                      ? 'bg-emerald-50/50 border-emerald-100'
                      : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                      ? 'bg-blue-50/50 border-blue-100'
                      : 'bg-rose-50/50 border-rose-100'
                  }`}>
                  <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Différence calculée</p>
                  <div className="flex items-end gap-2">
                    <p className={`text-2xl font-black tracking-tight ${
                      selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                        ? 'text-emerald-600'
                        : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                        ? 'text-blue-600'
                        : 'text-rose-600'
                    }`}>
                      {formatCurrency(selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel))}
                    </p>
                  </div>
                  <p className={`text-xs font-medium mt-1.5 ${
                      selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                        ? 'text-emerald-600'
                        : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                        ? 'text-blue-600'
                        : 'text-rose-600'
                    }`}>
                    {selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) === 0
                      ? '✓ Caisse parfaitement équilibrée'
                      : selectedEntry?.montant_caissier - parseFloat(adminFormData.montant_reel) > 0
                      ? '↑ Excédent (Plus)'
                      : '↓ Manque (Moins)'}
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="admin_notes" className="text-sm font-semibold text-gray-700">Notes Admin</Label>
                <div className="relative">
                  <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                    <FileText className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    id="admin_notes"
                    value={adminFormData.notes}
                    onChange={(e) => setAdminFormData({ ...adminFormData, notes: e.target.value })}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm text-sm min-h-[80px] resize-none"
                    placeholder="Commentaires de validation..."
                  />
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]">
                  <Calculator className="w-5 h-5 mr-2" />
                  Valider la Caisse
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog d'édition Sécurité Sociale */}
      <Dialog open={chifaDialogOpen} onOpenChange={setChifaDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingChifa ? 'Modifier' : 'Ajouter'} Paiement Sécurité Sociale</DialogTitle>
            <DialogDescription>
              Enregistrer un paiement CNAS, HORS CNAS, CASNOS, HORS CASNOS ou Caisse militaire
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChifaSubmit} className="space-y-4">
            <div>
              <Label htmlFor="chifa_date">Date et heure *</Label>
              <Input
                id="chifa_date"
                type="datetime-local"
                value={chifaFormData.date}
                onChange={(e) => setChifaFormData({ ...chifaFormData, date: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="chifa_category">Catégorie *</Label>
              <Select
                value={chifaFormData.category}
                onValueChange={(value) => setChifaFormData({ ...chifaFormData, category: value })}
              >
                <SelectTrigger id="chifa_category" className="mt-1">
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CNAS">CNAS</SelectItem>
                  <SelectItem value="HORS CNAS">HORS CNAS</SelectItem>
                  <SelectItem value="CASNOS">CASNOS</SelectItem>
                  <SelectItem value="HORS CASNOS">HORS CASNOS</SelectItem>
                  <SelectItem value="Caisse militaire">Caisse militaire</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="chifa_amount">Montant (DA) *</Label>
              <Input
                id="chifa_amount"
                type="number"
                step="0.01"
                min="0"
                value={chifaFormData.amount}
                onChange={(e) => setChifaFormData({ ...chifaFormData, amount: e.target.value })}
                required
                className="mt-1"
                placeholder="0.00"
              />
            </div>

            <div>
              <Label htmlFor="chifa_description">Description</Label>
              <Input
                id="chifa_description"
                type="text"
                value={chifaFormData.description}
                onChange={(e) => setChifaFormData({ ...chifaFormData, description: e.target.value })}
                className="mt-1"
                placeholder="Description du paiement..."
              />
            </div>

            <div>
              <Label htmlFor="chifa_reference">Référence</Label>
              <Input
                id="chifa_reference"
                type="text"
                value={chifaFormData.reference}
                onChange={(e) => setChifaFormData({ ...chifaFormData, reference: e.target.value })}
                className="mt-1"
                placeholder="N° de référence..."
              />
            </div>

            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setChifaDialogOpen(false);
                  resetChifaForm();
                }}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                {editingChifa ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Nouveau Wizard pour ajouter une entrée */}
      {wizardOpen && (
        <RevenueWizard 
          isOpen={wizardOpen}
          onClose={() => {
            setWizardOpen(false);
            setEditingCA(null);
          }}
          onSubmit={handleWizardSubmit}
          editMode={!!editingCA}
          initialData={editingCA}
          onSwitchToCashWizard={() => {
            setWizardOpen(false);
            setEditingCA(null);
            setCashWizardOpen(true);
          }}
        />
      )}

      {/* Cash Wizard pour l'onglet Clôture Caisse */}
      {cashWizardOpen && (
        <CashWizard
          isOpen={cashWizardOpen}
          onClose={() => {
            setCashWizardOpen(false);
            setSelectedEntry(null);
          }}
          onSubmit={handleCashWizardSubmit}
          users={users}
          editMode={!!selectedEntry}
          initialData={selectedEntry}
        />
      )}

      {/* Wizard Sécurité Sociale pour l'onglet chifa */}
      {chifaWizardOpen && (
        <SecuriteSocialeWizard
          isOpen={chifaWizardOpen}
          onClose={() => {
            setChifaWizardOpen(false);
            setEditingChifa(null);
          }}
          onSubmit={handleChifaWizardSubmit}
          editMode={!!editingChifa}
          initialData={editingChifa}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}

export default Revenues;
